"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.BracketParser = void 0;
const TYPE32_HTML_HEADER = /(\d+) (\d+) (\d+) /y;
class BracketParser {
    constructor(data) {
        this.data = data;
        this.cursor = 0;
    }
    parse() {
        this.cursor = 0;
        return { nodes: this.parseLevel(null) };
    }
    parseLevel(closeKind) {
        const nodes = [];
        while (!this.isEof()) {
            this.skipWhitespace();
            if (this.isEof()) {
                break;
            }
            if (closeKind !== null) {
                if (this.peek(2) === '/>') {
                    this.cursor += 2;
                    this.consumeSpaces(2);
                    break;
                }
                if (closeKind === 'econtainer' && this.peek(3) === 'e/>') {
                    this.cursor += 3;
                    this.consumeSpaces(2);
                    break;
                }
            }
            if (this.peek(1) === '<') {
                const node = this.parseBracket();
                nodes.push(node);
                if (node.type === 'atom' && node.content === 'zeronull') {
                    this.parseZeronullFollowOn(nodes);
                    continue;
                }
                if (node.type === 'atom' && node.content === '9' && !this.isEof() && /\d/.test(this.data[this.cursor])) {
                    nodes.push(this.parseType32Blob());
                    continue;
                }
            }
            else {
                const start = this.cursor;
                while (!this.isEof()) {
                    if (this.peek(1) === '<') {
                        break;
                    }
                    if (closeKind !== null && this.peek(2) === '/>') {
                        break;
                    }
                    if (closeKind === 'econtainer' && this.peek(3) === 'e/>') {
                        break;
                    }
                    this.cursor++;
                }
                const text = this.data.slice(start, this.cursor).trim();
                if (text.length > 0) {
                    nodes.push({ type: 'text', content: text });
                }
            }
        }
        return nodes;
    }
    parseBracket() {
        this.cursor++;
        const next2 = this.peek(2);
        if (next2 === '  ') {
            this.cursor += 2;
            const children = this.parseLevel('container');
            return { type: 'container', children };
        }
        if (next2 === 'e ') {
            this.cursor += 2;
            const children = this.parseLevel('econtainer');
            return { type: 'econtainer', children };
        }
        if (next2 === 'n ') {
            this.cursor += 2;
            const content = this.findAndConsume(' n/>');
            this.consumeSpaces(2);
            return { type: 'name', content };
        }
        if (next2 === 'v ') {
            this.cursor += 2;
            const content = this.findAndConsume(' v/>');
            this.consumeSpaces(3);
            return { type: 'value', content };
        }
        const content = this.findAndConsume('/>').trim();
        this.consumeSpaces(2);
        return { type: 'atom', content };
    }
    parseZeronullFollowOn(nodes) {
        this.skipWhitespace();
        if (!this.isEof() && this.peek(1) === '<') {
            nodes.push(this.parseBracket());
        }
        this.skipWhitespace();
        if (this.isEof() || this.peek(1) !== '<') {
            return;
        }
        const lengthNode = this.parseBracket();
        nodes.push(lengthNode);
        if (lengthNode.type !== 'atom') {
            return;
        }
        const byteLen = parseInt(lengthNode.content, 10);
        if (isNaN(byteLen) || byteLen <= 0) {
            nodes.push({ type: 'atom', content: '' });
            return;
        }
        this.skipWhitespace();
        nodes.push(this.parseAtomByCount(byteLen));
    }
    parseAtomByCount(byteLen) {
        if (this.peek(1) === '<') {
            this.cursor++;
        }
        if (this.peek(1) === ' ') {
            this.cursor++;
        }
        const endChar = this.charOffsetForUtf8Bytes(this.cursor, byteLen);
        const content = this.data.slice(this.cursor, endChar);
        this.cursor = endChar;
        if (this.peek(1) === ' ') {
            this.cursor++;
        }
        if (this.peek(2) === '/>') {
            this.cursor += 2;
        }
        this.consumeSpaces(2);
        return { type: 'atom', content };
    }
    parseType32Blob() {
        TYPE32_HTML_HEADER.lastIndex = this.cursor;
        const match = TYPE32_HTML_HEADER.exec(this.data);
        if (match === null) {
            return { type: 'raw-blob', x: 0, y: 0, content: '' };
        }
        const x = parseInt(match[1], 10);
        const y = parseInt(match[2], 10);
        const byteLen = parseInt(match[3], 10);
        this.cursor = TYPE32_HTML_HEADER.lastIndex;
        const content = this.consumeByCount(byteLen);
        this.consumeSpaces(2);
        return { type: 'raw-blob', x, y, content };
    }
    consumeByCount(byteLen) {
        const endChar = this.charOffsetForUtf8Bytes(this.cursor, byteLen);
        const content = this.data.slice(this.cursor, endChar);
        this.cursor = endChar;
        return content;
    }
    charOffsetForUtf8Bytes(startChar, byteLen) {
        let bytesConsumed = 0;
        let i = startChar;
        while (i < this.data.length) {
            const code = this.data.codePointAt(i) ?? 0;
            const charBytes = code <= 0x7f ? 1
                : code <= 0x7ff ? 2
                    : code <= 0xffff ? 3
                        : 4;
            if (bytesConsumed + charBytes > byteLen) {
                break;
            }
            bytesConsumed += charBytes;
            i += code > 0xffff ? 2 : 1;
        }
        return i;
    }
    isEof() {
        return this.cursor >= this.data.length;
    }
    peek(width) {
        return this.data.slice(this.cursor, this.cursor + width);
    }
    skipWhitespace() {
        while (!this.isEof() &&
            (this.data[this.cursor] === ' ' ||
                this.data[this.cursor] === '\n' ||
                this.data[this.cursor] === '\r' ||
                this.data[this.cursor] === '\t')) {
            this.cursor++;
        }
    }
    consumeSpaces(max) {
        let consumed = 0;
        while (consumed < max && !this.isEof() && this.data[this.cursor] === ' ') {
            this.cursor++;
            consumed++;
        }
    }
    findAndConsume(sentinel) {
        const idx = this.data.indexOf(sentinel, this.cursor);
        if (idx < 0) {
            const remainder = this.data.slice(this.cursor);
            this.cursor = this.data.length;
            return remainder;
        }
        const slice = this.data.slice(this.cursor, idx);
        this.cursor = idx + sentinel.length;
        return slice;
    }
}
exports.BracketParser = BracketParser;
//# sourceMappingURL=parser.js.map